import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  ApiGarGarsListRequest,
  FieldMeasurement as OpenAPIFieldMeasurement,
} from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { fetchAllPages } from "./helpers/fetch-all-pages";
import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getFieldMeasurementSingleQuery = (uuid: string) => ({
  queryKey: ["gar-field-measurement-single", uuid],
  queryFn: () => uploadApi.apiGarFieldmeasurementsRead({ uuid }),
});

export const useGarFieldMeasurementSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<OpenAPIFieldMeasurement>(
      queryClient,
      ["gar-field-measurement-list"],
      uuid,
      getFieldMeasurementSingleQuery(uuid)
    );
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGarFieldMeasurementListQuery = (options: ApiGarGarsListRequest) => ({
  queryKey: ["gar-field-measurement-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGarFieldmeasurementsList(options),
  placeholderData: keepPreviousData,
});

export const useGarFieldMeasurementListQuery = (options: ApiGarGarsListRequest) => {
  return useQuery(getGarFieldMeasurementListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGarFieldMeasurementQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["gar-field-measurement-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGarFieldMeasurementListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  }, [queryClient]);
};

export const useGarFieldMeasurementsAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (garUuid: string): Promise<OpenAPIFieldMeasurement[]> =>
    queryClient
      .fetchQuery({
        queryKey: ["gar-field-measurement-all-pages", garUuid],
        queryFn: () =>
          fetchAllPages(({ page }) =>
            uploadApi.apiGarFieldmeasurementsList({ gar: garUuid, page })
          ),
      })
      .then((r) => r.results);
};
